import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getDoctorPatients } from '../api/doctorPatientsApi';
import Pagination from '../components/Pagination';
import { formatDateTime, formatPatientName, normalizeUiLocale } from '../utils/locale';

const PatientList = () => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');
  const [snapshotFilter, setSnapshotFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [statusCounts, setStatusCounts] = useState({
    all: 0,
    has_snapshot: 0,
    missing_snapshot: 0
  });

  const { t, i18n } = useTranslation();
  const locale = normalizeUiLocale(i18n.resolvedLanguage || i18n.language);
  const navigate = useNavigate();

  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalCount / pageSize)), [pageSize, totalCount]);

  const formatDate = useCallback((value) => {
    if (!value) {
      return '-';
    }
    return formatDateTime(value, locale) || '-';
  }, [locale]);

  const formatVisitType = (visitType) => {
    if (visitType === 'follow_up') return t('patients.visitTypeFollowUp');
    if (visitType === 'checkup') return t('patients.visitTypeCheckup');
    if (visitType === 'initial') return t('patients.visitTypeInitial');
    return t('patients.visitTypeUnknown');
  };

  const formatVisitStatus = (status) => {
    if (status === 'complete') return t('patients.visitStatusComplete');
    if (status === 'ongoing') return t('patients.visitStatusOngoing');
    if (status === 'started') return t('patients.visitStatusStarted');
    if (status === 'refreshed') return t('patients.visitStatusRefreshed');
    if (status === 'canceled') return t('patients.visitStatusCanceled');
    return status || t('common.unknown');
  };

  const getVisitStatusClass = (status) => {
    if (status === 'complete') return 'status-pill status-pill--success';
    if (status === 'ongoing' || status === 'started') return 'status-pill status-pill--warning';
    if (status === 'canceled') return 'status-pill status-pill--danger';
    return 'status-pill status-pill--neutral';
  };

  const getPatientName = (patient) => {
    if (!patient) {
      return t('common.unknown');
    }
    return formatPatientName(patient, locale, patient.patient_id || `#${patient.id}`);
  };

  const fetchPatients = useCallback(async ({
    page = currentPage,
    size = pageSize,
    nextQuery = appliedQuery,
    nextSnapshotFilter = snapshotFilter
  } = {}) => {
    try {
      setLoading(true);
      setError('');
      const offset = (page - 1) * size;
      const response = await getDoctorPatients({
        limit: size,
        offset,
        q: nextQuery,
        snapshot: nextSnapshotFilter
      });
      setPatients(response.items || []);
      setTotalCount(response.total || 0);
      setCurrentPage(page);
      setPageSize(size);

      if (nextSnapshotFilter === 'all') {
        const items = response.items || [];
        setStatusCounts({
          all: response.total || 0,
          has_snapshot: items.reduce((count, row) => count + ((row.snapshot_count || 0) > 0 ? 1 : 0), 0),
          missing_snapshot: items.reduce((count, row) => count + ((row.snapshot_count || 0) === 0 ? 1 : 0), 0)
        });
      }
    } catch (requestError) {
      console.error('获取患者列表失败:', requestError);
      setError(`${t('patients.fetchListError')}: ${requestError.response?.data?.error || t('common.unknownError')}`);
    } finally {
      setLoading(false);
    }
  }, [appliedQuery, currentPage, pageSize, snapshotFilter, t]);

  useEffect(() => {
    fetchPatients({ page: 1, size: pageSize, nextQuery: appliedQuery, nextSnapshotFilter: snapshotFilter });
  }, [appliedQuery, snapshotFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (event) => {
    event.preventDefault();
    setAppliedQuery(query.trim());
    setCurrentPage(1);
  };

  const handleReset = () => {
    setQuery('');
    setAppliedQuery('');
    setSnapshotFilter('all');
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    fetchPatients({ page, size: pageSize });
  };

  const handlePageSizeChange = (nextPageSize) => {
    fetchPatients({ page: 1, size: nextPageSize });
  };

  const handleSnapshotFilterChange = (value) => {
    setSnapshotFilter(value);
    setCurrentPage(1);
  };

  return (
    <div className="page-shell patient-page patient-list-page">
      <div className="page-header">
        <div className="page-title-block">
          <h1 className="page-title">{t('patients.listTitle')}</h1>
        </div>
        <div className="page-actions">
          <button
            type="button"
            className="theme-button theme-button--secondary"
            onClick={() => fetchPatients()}
            disabled={loading}
          >
            {t('common.refresh')}
          </button>
        </div>
      </div>

      {error && <div className="alert alert--danger">{error}</div>}

      <section className="surface-panel">
        <div className="surface-panel__body stack">
          <div className="patient-filter-summary">
            <button
              type="button"
              className={`status-pill ${snapshotFilter === 'all' ? 'status-pill--cool' : 'status-pill--neutral'}`}
              onClick={() => handleSnapshotFilterChange('all')}
            >
              {t('patients.allPatients')} {statusCounts.all || totalCount}
            </button>
            <button
              type="button"
              className={`status-pill ${snapshotFilter === 'has_snapshot' ? 'status-pill--cool' : 'status-pill--neutral'}`}
              onClick={() => handleSnapshotFilterChange('has_snapshot')}
            >
              {t('patients.hasSnapshot')} {snapshotFilter === 'has_snapshot' ? totalCount : statusCounts.has_snapshot}
            </button>
            <button
              type="button"
              className={`status-pill ${snapshotFilter === 'missing_snapshot' ? 'status-pill--warning' : 'status-pill--neutral'}`}
              onClick={() => handleSnapshotFilterChange('missing_snapshot')}
            >
              {t('patients.missingSnapshot')} {snapshotFilter === 'missing_snapshot' ? totalCount : statusCounts.missing_snapshot}
            </button>
          </div>

          <form className="patient-search-row" onSubmit={handleSearch}>
            <div className="patient-search-box">
              <span className="patient-search-box__icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
              </span>
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t('patients.searchPlaceholder')}
                className="theme-input"
              />
            </div>
            <div className="segmented-control" role="group" aria-label={t('patients.snapshotFilter')}>
              {['all', 'has_snapshot', 'missing_snapshot'].map((value) => (
                <button
                  key={value}
                  type="button"
                  className={snapshotFilter === value ? 'is-active' : ''}
                  onClick={() => handleSnapshotFilterChange(value)}
                >
                  {value === 'all' && t('patients.filterAll')}
                  {value === 'has_snapshot' && t('patients.filterHasSnapshot')}
                  {value === 'missing_snapshot' && t('patients.filterMissingSnapshot')}
                </button>
              ))}
            </div>
            <button type="submit" className="theme-button">
              {t('common.search')}
            </button>
            <button type="button" className="theme-button theme-button--secondary" onClick={handleReset}>
              {t('common.reset')}
            </button>
          </form>

          {loading ? (
            <div className="loading-state">{t('common.loading')}</div>
          ) : patients.length === 0 ? (
            <div className="empty-state">
              <h3>{t('patients.noPatients')}</h3>
              <p>{t('patients.noPatientsHint')}</p>
            </div>
          ) : (
            <div className="data-table-shell">
              <table className="data-table patient-table">
                <thead>
                  <tr>
                    <th>{t('patients.patient')}</th>
                    <th>{t('patients.contact')}</th>
                    <th>{t('patients.latestVisit')}</th>
                    <th>{t('patients.timeline')}</th>
                    <th>{t('patients.latestSnapshot')}</th>
                    <th>{t('reports.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {patients.map((row) => {
                    const patient = row.patient || {};
                    const latestVisit = row.latest_visit;
                    return (
                      <tr key={patient.id}>
                        <td>
                          <div className="table-composite">
                            <span className="table-title"><bdi dir="auto">{getPatientName(patient)}</bdi></span>
                            <span className="table-meta">{t('patients.patientId')}: {patient.patient_id || `#${patient.id}`}</span>
                            <span className="table-meta">{t('patients.mrn')}: {patient.mrn || '-'}</span>
                          </div>
                        </td>
                        <td>
                          <div className="table-composite">
                            <span>{patient.phone_e164 || patient.phone || '-'}</span>
                            <span className="table-meta">{patient.email || '-'}</span>
                          </div>
                        </td>
                        <td>
                          {latestVisit ? (
                            <div className="table-composite">
                              <span>#{latestVisit.id}</span>
                              <span className="table-meta">{formatDate(latestVisit.start_time)}</span>
                              <span className="table-meta--inline">
                                <span className="status-pill status-pill--cool">{formatVisitType(latestVisit.visit_type)}</span>
                                <span className={getVisitStatusClass(latestVisit.status)}>{formatVisitStatus(latestVisit.status)}</span>
                              </span>
                            </div>
                          ) : (
                            <span className="table-meta">{t('patients.noVisits')}</span>
                          )}
                        </td>
                        <td>
                          <div className="patient-timeline-summary">
                            <div className="patient-timeline-summary__primary">
                              <span className="patient-count-main">{row.visit_count || 0}</span>
                              <span>{t('patients.visits')}</span>
                            </div>
                            <div className="patient-timeline-summary__meta">
                              <span>{row.completed_visit_count || 0} {t('patients.completed')}</span>
                              <span className={(row.snapshot_count || 0) > 0 ? 'patient-count-accent' : ''}>
                                {row.snapshot_count || 0} {(row.snapshot_count || 0) > 0 ? t('patients.snapshots') : t('patients.noSnapshot')}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>{formatDate(row.latest_snapshot_at)}</td>
                        <td>
                          <button
                            type="button"
                            className="table-link-button"
                            onClick={() => navigate(`/patients/${patient.id}`)}
                          >
                            {t('patients.viewTimeline')}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={[10, 20, 50]}
            showQuickJumper={false}
          />
        </div>
      </section>
    </div>
  );
};

export default PatientList;
